import imageCompression from 'browser-image-compression';

type NavigatorWithDeviceMemory = Navigator & {
  deviceMemory?: number;
};

const isWebWorkerSupported = () => {
  try {
    return (
      typeof Worker !== 'undefined' &&
      !navigator.userAgent.includes('Telegram') &&
      !navigator.userAgent.includes('WebView')
    );
  } catch {
    return false;
  }
};

const isImageCompressionSupported = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return false;
    if (!window.File || !window.FileReader) return false;
    if (!window.Blob) return false;

    return true;
  } catch {
    return false;
  }
};

const getDeviceMemoryLimit = () => {
  try {
    const memory = (navigator as NavigatorWithDeviceMemory).deviceMemory;

    if (memory !== undefined) {
      if (memory <= 2) return 0.5;
      if (memory <= 4) return 1;
    }

    const isLowEndDevice =
      /Android.*Chrome\/[0-5]/.test(navigator.userAgent) ||
      /iPhone.*OS [0-9]/.test(navigator.userAgent);

    return isLowEndDevice ? 0.5 : 1;
  } catch {
    return 1;
  }
};

export const compressImage = async (file: File): Promise<File> => {
  if (!isImageCompressionSupported()) {
    console.warn('Image compression not supported, returning original file');
    return file;
  }

  const memoryLimit = getDeviceMemoryLimit();
  const options = {
    maxSizeMB: memoryLimit,
    maxWidthOrHeight: 1024,
    useWebWorker: isWebWorkerSupported(),
  };

  try {
    if (!file || !file.type.startsWith('image/')) {
      console.log('Invalid file type in compressImage:', file?.type);
      const supportedFormats = ['jpg', 'jpeg', 'png'];

      if (file.type.includes('pdf')) {
        const errorMessage =
          'PDF файлы не поддерживаются. Поддерживаемые форматы: jpg, jpeg, png';
        console.log('Throwing PDF error:', errorMessage);
        throw new Error(errorMessage);
      } else if (file.type.includes('video')) {
        const errorMessage =
          'Видео файлы не поддерживаются. Поддерживаемые форматы: jpg, jpeg, png';
        console.log('Throwing video error:', errorMessage);
        throw new Error(errorMessage);
      } else {
        const errorMessage = `Неподдерживаемый формат файла. Поддерживаемые форматы: ${supportedFormats.join(', ')}`;
        console.log('Throwing generic format error:', errorMessage);
        throw new Error(errorMessage);
      }
    }

    if (file.size > 50 * 1024 * 1024) {
      throw new Error('File too large for compression. Maximum size is 50MB.');
    }

    const compressedFile = await imageCompression(file, options);

    if (!compressedFile || compressedFile.size === 0) {
      throw new Error('Compression resulted in empty file');
    }

    return compressedFile;
  } catch (error) {
    console.error('Compression error:', error);

    if (file.size > 10 * 1024 * 1024) {
      throw new Error(
        'Файл слишком большой для обработки. Максимальный размер: 10 МБ',
      );
    }

    console.warn('Compression failed, using original file:', error);
    return file;
  }
};
